// Lightweight, privacy-safe device fingerprint
// Combines low-entropy, stable attributes and hashes them with SHA-256.
// Cached in localStorage to avoid recomputation across sessions.

function toHex(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

async function sha256Hex(text: string) {
  if (typeof window === 'undefined' || !(window as any).crypto?.subtle) {
    // Fallback: simple hash-like number if subtle crypto not available
    let h = 0;
    for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) >>> 0;
    return h.toString(16);
  }
  const enc = new TextEncoder();
  const data = enc.encode(text);
  const digest = await (window as any).crypto.subtle.digest('SHA-256', data);
  return toHex(digest);
}

export async function getDeviceFingerprint(): Promise<string> {
  try {
    if (typeof window === 'undefined') return '';
    const key = 'ud_device_fp_v1';
    const cached = window.localStorage.getItem(key);
    if (cached) return cached;

    const nav = window.navigator || ({} as Navigator);
    const screenObj = window.screen || ({} as Screen);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const parts = [
      nav.userAgent || '',
      (nav as any).platform || '',
      (nav as any).language || (nav as any).languages?.join(',') || '',
      String(screenObj.width || '') + 'x' + String(screenObj.height || '') + 'x' + String((screenObj as any).colorDepth || ''),
      String((window as any).devicePixelRatio || ''),
      tz,
    ];

    // Small canvas text metric to add a tiny bit more entropy without invasive techniques
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.textBaseline = 'top';
        ctx.font = "14px 'Arial'";
        ctx.fillText(nav.userAgent || 'ua', 2, 2);
        parts.push(canvas.toDataURL().slice(0, 64));
      }
    } catch {}

    const raw = parts.join('|');
    const fp = await sha256Hex(raw);
    window.localStorage.setItem(key, fp);
    return fp;
  } catch {
    return '';
  }
}
